/**
 * Classification Tier Chart Component
 *
 * Bar chart showing classification counts broken down by tier
 */

import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { ChartCard, CHART_HEIGHT_CLASSES } from './ChartCard';
import { METRIC_DESCRIPTIONS } from './MetricDescriptions';
import type { ClassificationByTier } from '@/types/dashboard';

interface ClassificationTierChartProps {
  data: ClassificationByTier | undefined;
  isLoading: boolean;
}

// Human-readable tier labels
const TIER_CONFIG: Array<{
  key: keyof ClassificationByTier;
  label: string;
  color: string;
}> = [
  { key: 'contactMatch', label: 'Contact Match', color: 'hsl(142, 76%, 36%)' }, // green
  { key: 'nameMatch', label: 'Name Match', color: 'hsl(217, 91%, 60%)' }, // blue
  { key: 'keywordMatch', label: 'Keyword Match', color: 'hsl(262, 83%, 58%)' }, // purple
  { key: 'semanticMatch', label: 'Semantic Match', color: 'hsl(31, 97%, 72%)' }, // orange
  { key: 'none', label: 'No Match', color: 'hsl(0, 84%, 60%)' }, // red
];

const chartConfig = {
  count: {
    label: 'Classifications',
    color: 'hsl(var(--chart-1))',
  },
} satisfies ChartConfig;

export function ClassificationTierChart({
  data,
  isLoading,
}: ClassificationTierChartProps) {
  // Transform data for recharts
  const chartData = data
    ? TIER_CONFIG.map(({ key, label, color }) => ({
        tier: label,
        count: data[key],
        fill: color,
      })).filter((item) => item.count > 0)
    : [];

  // Show skeleton when loading OR when data hasn't arrived yet
  const showSkeleton = isLoading || data === undefined;

  // Show "no data" only when data has loaded but is genuinely empty
  const isEmpty = !showSkeleton && chartData.length === 0;

  return (
    <ChartCard
      title="Classifications by Tier"
      tooltip={METRIC_DESCRIPTIONS.classificationsByTier}
      isLoading={showSkeleton}
      isEmpty={isEmpty}
      emptyMessage="No classification data available for the selected time range"
      emptyAriaLabel="No classification data available"
    >
      <ChartContainer
        config={chartConfig}
        className={`${CHART_HEIGHT_CLASSES} w-full`}
      >
        <BarChart data={chartData} layout="vertical" accessibilityLayer>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis
            type="number"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fontSize: 12 }}
            allowDecimals={false}
          />
          <YAxis
            type="category"
            dataKey="tier"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fontSize: 12 }}
            width={100}
          />
          <ChartTooltip content={<ChartTooltipContent hideLabel />} />
          <Bar dataKey="count" name="Classifications" radius={[0, 4, 4, 0]} />
        </BarChart>
      </ChartContainer>
    </ChartCard>
  );
}
